<template>
  <v-app>
    <v-main>
      <v-container class="new-topic-container">
        <!-- Breadcrumbs -->
        <v-breadcrumbs :items="breadcrumbs" class="px-0 mb-2">
          <template v-slot:prepend>
            <v-icon icon="mdi-home" class="mr-2"></v-icon>
          </template>
          <template v-slot:divider>
            <v-icon icon="mdi-chevron-right" size="small"></v-icon>
          </template>
        </v-breadcrumbs>

        <!-- Page Title -->
        <h1 class="text-h4 font-weight-bold mb-4">{{ pageTitle }}</h1>

        <!-- Form -->
        <v-form ref="form" @submit.prevent="handleSubmit">
          <!-- User Fields (not signed in) -->
          <template v-if="!userSignedIn">
            <v-text-field
              v-model="formData.email"
              :label="t.email"
              :placeholder="t.email"
              type="email"
              variant="outlined"
              density="comfortable"
              class="mb-2"
              :rules="[rules.required, rules.email]"
              autofocus
            ></v-text-field>

            <v-text-field
              v-model="formData.name"
              :label="t.name"
              :placeholder="t.name"
              variant="outlined"
              density="comfortable"
              class="mb-2"
              :rules="[rules.required]"
            ></v-text-field>
          </template>

          <!-- Privacy Selection (when both forums and tickets enabled) -->
          <template v-if="showPrivacyOption">
            <label class="text-body-1 d-block mb-2">{{ t.should_message_be_private }}</label>
            <v-radio-group v-model="formData.isPrivate" class="mb-4">
              <v-radio :label="t.only_support_can_respond" :value="true"></v-radio>
              <v-radio :label="t.responses_can_come_from_everyone" :value="false"></v-radio>
            </v-radio-group>
          </template>

          <!-- Forum Selection (when forums enabled) -->
          <v-select
            v-if="showForumSelect"
            v-model="formData.forumId"
            :items="forumItems"
            item-title="name"
            item-value="id"
            :label="t.forum_name"
            variant="outlined"
            density="comfortable"
            class="mb-2"
          ></v-select>

          <!-- Team Assignment (when teams enabled and tickets enabled) -->
          <v-select
            v-if="showTeamSelect"
            v-model="formData.teamList"
            :items="teams"
            :label="t.assign_to"
            variant="outlined"
            density="comfortable"
            class="mb-2"
            clearable
          ></v-select>

          <!-- Subject -->
          <v-text-field
            v-model="formData.subject"
            :label="t.subject"
            :placeholder="t.subject"
            variant="outlined"
            density="comfortable"
            class="mb-2"
            :rules="[rules.required]"
            @update:model-value="onSubjectChange"
          ></v-text-field>

          <!-- Auto-suggest Results -->
          <v-card
            v-if="suggestions.length > 0"
            variant="outlined"
            class="mb-4 suggestions-card"
          >
            <v-card-text>
              <strong>{{ t.asked_before }}</strong>
              <v-list density="compact" class="mt-2">
                <v-list-item
                  v-for="(suggestion, index) in suggestions"
                  :key="index"
                  :href="suggestion.link"
                  target="_blank"
                  class="suggestion-item"
                >
                  <template v-slot:prepend>
                    <v-icon size="small" color="primary">mdi-file-document-outline</v-icon>
                  </template>
                  <v-list-item-title class="text-primary">{{ suggestion.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>

          <!-- Message Body -->
          <v-textarea
            v-model="formData.body"
            :label="t.message"
            :placeholder="t.how_can_we_help"
            variant="outlined"
            density="comfortable"
            rows="8"
            class="mb-2"
            :rules="[rules.required]"
          ></v-textarea>

          <!-- File Attachments -->
          <div class="mb-4">
            <v-btn
              variant="outlined"
              color="grey-darken-1"
              prepend-icon="mdi-paperclip"
              :disabled="formData.attachments.length >= MAX_FILES"
              @click="$refs.fileInput.click()"
            >
              {{ t.add_screenshots }}
            </v-btn>
            <input
              ref="fileInput"
              type="file"
              multiple
              style="display: none;"
              @change="onFilesSelected"
            />
            <div class="text-caption text-grey mt-1">
              Max {{ MAX_FILES }} files, {{ MAX_FILE_SIZE_MB }} MB each
            </div>

            <!-- Accumulated file list -->
            <div v-if="formData.attachments.length > 0" class="mt-3 d-flex flex-wrap ga-2">
              <v-chip
                v-for="(file, index) in formData.attachments"
                :key="`${file.name}-${file.size}-${index}`"
                variant="outlined"
                @click="removeFile(index)"
                style="cursor: pointer;"
              >
                <v-icon start size="small">mdi-file</v-icon>
                {{ file.name }}
                <span class="text-grey ml-1">({{ formatFileSize(file.size) }})</span>
                <v-icon end size="small" class="ml-2">mdi-close-circle</v-icon>
              </v-chip>
            </div>

            <div v-if="fileError" class="text-error text-body-2 mt-2">{{ fileError }}</div>
          </div>

          <!-- Honeypot (hidden from users, visible to bots) -->
          <div style="position: absolute; left: -9999px;">
            <input v-model="formData.honeypot" type="url" name="topic[url]" placeholder="http://" />
          </div>

          <!-- reCAPTCHA widget placeholder (standard recaptcha) -->
          <div v-if="recaptchaEnabled && !userSignedIn" ref="recaptchaContainer" class="mb-4"></div>

          <!-- Submit Button -->
          <v-btn
            type="submit"
            color="warning"
            size="large"
            :loading="submitting"
            :disabled="submitting"
          >
            {{ t.submit_start_discussion }}
          </v-btn>
        </v-form>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
export default {
  name: 'NewTopicPage',
  props: {
    formAction: { type: String, default: '/topics' },
    csrfToken: { type: String, default: '' },
    userSignedIn: { type: Boolean, default: false },
    forumsEnabled: { type: Boolean, default: false },
    ticketsEnabled: { type: Boolean, default: false },
    teamsEnabled: { type: Boolean, default: false },
    forums: { type: Array, default: () => [] },
    teams: { type: Array, default: () => [] },
    defaultPrivate: { type: String, default: '0' },
    recaptchaEnabled: { type: Boolean, default: false },
    recaptchaEnterpriseEnabled: { type: Boolean, default: false },
    recaptchaEnterpriseSiteKey: { type: String, default: '' },
    extensionWhitelist: { type: String, default: '' },
    extensionBlacklist: { type: String, default: '' },
    docId: { type: String, default: '' },
    fromWidget: { type: Boolean, default: false },
    translations: { type: Object, default: () => ({}) },
    searchPath: { type: String, default: '/search.json' },
    pageTitle: { type: String, default: 'Open a Ticket' },
    resultPath: { type: String, default: '/result' },
    knowledgebasePath: { type: String, default: '/knowledgebase' }
  },
  data() {
    return {
      MAX_FILES: 5,
      MAX_FILE_SIZE_MB: 10,
      MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024, // 10 MB
      formData: {
        email: '',
        name: '',
        isPrivate: this.defaultPrivate !== '0',
        forumId: this.forums.length > 0 ? this.forums[0].id : null,
        teamList: '',
        subject: '',
        body: '',
        attachments: [],
        honeypot: ''
      },
      suggestions: [],
      suggestTimer: null,
      submitting: false,
      fileError: '',
      rules: {
        required: v => !!v || 'This field is required',
        email: v => /.+@.+\..+/.test(v) || 'Please enter a valid email'
      }
    }
  },
  computed: {
    t() {
      return {
        email: this.translations.email || 'Email',
        name: this.translations.name || 'Name',
        subject: this.translations.subject || 'Subject',
        message: this.translations.message || 'Message',
        how_can_we_help: this.translations.how_can_we_help || 'How can we help?',
        should_message_be_private: this.translations.should_message_be_private || 'Should this message be private?',
        only_support_can_respond: this.translations.only_support_can_respond || 'Only support can respond',
        responses_can_come_from_everyone: this.translations.responses_can_come_from_everyone || 'Responses can come from everyone',
        forum_name: this.translations.forum_name || 'Forum',
        assign_to: this.translations.assign_to || 'Assign to',
        submit_start_discussion: this.translations.submit_start_discussion || 'Start Discussion',
        add_screenshots: this.translations.add_screenshots || 'Add screenshots',
        asked_before: this.translations.asked_before || 'Has your question been answered before? We think these may be similar',
        home: this.translations.home || 'Home'
      }
    },
    breadcrumbs() {
      return [
        { title: this.t.home, href: '/' },
        { title: this.pageTitle, disabled: true }
      ]
    },
    showPrivacyOption() {
      // Show privacy option when both forums and tickets are enabled
      // AND (user is signed in OR recaptcha is available)
      return this.forumsEnabled && this.ticketsEnabled &&
        (this.userSignedIn || this.recaptchaEnabled || this.recaptchaEnterpriseEnabled)
    },
    showForumSelect() {
      // Show forum select when forums are enabled
      // AND (user is signed in OR recaptcha is available)
      // AND (both forums and tickets enabled, OR forums only)
      if (!this.forumsEnabled) return false
      if (!this.userSignedIn && !this.recaptchaEnabled && !this.recaptchaEnterpriseEnabled) return false
      return true
    },
    showTeamSelect() {
      return this.teamsEnabled && this.ticketsEnabled && this.teams.length > 0
    },
    forumItems() {
      return this.forums.map(f => ({
        id: f.id,
        name: f.name
      }))
    },
    whitelistExtensions() {
      if (!this.extensionWhitelist) return []
      return this.extensionWhitelist.split(',').map(e => e.trim().toLowerCase()).filter(Boolean)
    },
    blacklistExtensions() {
      if (!this.extensionBlacklist) return []
      return this.extensionBlacklist.split(',').map(e => e.trim().toLowerCase()).filter(Boolean)
    },
    effectivePrivate() {
      // Determine the effective private value based on feature flags
      if (this.forumsEnabled && this.ticketsEnabled) {
        return this.formData.isPrivate
      } else if (this.ticketsEnabled && !this.forumsEnabled) {
        return true
      } else if (this.forumsEnabled && !this.ticketsEnabled) {
        return false
      }
      // Default: private
      return true
    },
    effectiveForumId() {
      if (this.showForumSelect) {
        return this.formData.forumId
      }
      // Default to forum 1 when only tickets are enabled
      return 1
    }
  },
  mounted() {
    // Load reCAPTCHA Enterprise script if needed
    if (this.recaptchaEnterpriseEnabled && !this.userSignedIn) {
      this.loadRecaptchaEnterprise()
    }
  },
  methods: {
    onSubjectChange(value) {
      // Debounced auto-suggest
      clearTimeout(this.suggestTimer)
      if (!value || value.length < 3) {
        this.suggestions = []
        return
      }
      this.suggestTimer = setTimeout(() => {
        this.fetchSuggestions(value)
      }, 300)
    },

    async fetchSuggestions(query) {
      try {
        // Extract locale from current URL path (e.g., /en/topics/new -> en)
        const pathParts = window.location.pathname.split('/')
        const locale = pathParts[1] || 'en'
        const url = `/${locale}/search.json?depth=5&query=${encodeURIComponent(query)}`
        const response = await fetch(url)
        if (response.ok) {
          const data = await response.json()
          this.suggestions = data || []
        }
      } catch (e) {
        // Silently fail - suggestions are optional
        this.suggestions = []
      }
    },

    onFilesSelected(event) {
      const newFiles = Array.from(event.target.files || [])
      this.fileError = ''

      for (const file of newFiles) {
        // Check max file count
        if (this.formData.attachments.length >= this.MAX_FILES) {
          this.fileError = `You can attach up to ${this.MAX_FILES} files.`
          break
        }

        // Check individual file size
        if (file.size > this.MAX_FILE_SIZE_BYTES) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
          this.fileError = `"${file.name}" is ${sizeMB} MB. Maximum file size is ${this.MAX_FILE_SIZE_MB} MB.`
          continue
        }

        // Check extension whitelist/blacklist
        const ext = file.name.split('.').pop().toLowerCase()
        if (this.whitelistExtensions.length > 0 && !this.whitelistExtensions.includes(ext)) {
          this.fileError = `File type .${ext} is not allowed. Allowed types: ${this.extensionWhitelist}`
          continue
        }
        if (this.blacklistExtensions.length > 0 && this.blacklistExtensions.includes(ext)) {
          this.fileError = `File type .${ext} is not allowed.`
          continue
        }

        // Skip duplicates
        const isDuplicate = this.formData.attachments.some(f => f.name === file.name && f.size === file.size)
        if (!isDuplicate) {
          this.formData.attachments.push(file)
        }
      }

      // Reset file input so the same file can be re-selected
      event.target.value = ''
    },

    removeFile(index) {
      this.formData.attachments = this.formData.attachments.filter((_, i) => i !== index)
      this.fileError = ''
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },

    loadRecaptchaEnterprise() {
      if (document.querySelector('script[src*="recaptcha/enterprise"]')) return
      const script = document.createElement('script')
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.recaptchaEnterpriseSiteKey}`
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    },

    async handleSubmit() {
      // Validate form
      const { valid } = await this.$refs.form.validate()
      if (!valid) return

      // Check for file errors
      if (this.fileError) return

      this.submitting = true

      // Handle reCAPTCHA Enterprise
      if (this.recaptchaEnterpriseEnabled && !this.userSignedIn) {
        try {
          await this.submitWithRecaptchaEnterprise()
        } catch (err) {
          console.error('[Captcha] reCAPTCHA Enterprise error:', err)
          this.submitting = false
        }
        return
      }

      // Standard submit
      this.submitForm()
    },

    async submitWithRecaptchaEnterprise() {
      return new Promise((resolve, reject) => {
        if (typeof grecaptcha === 'undefined' || !grecaptcha.enterprise) {
          // If reCAPTCHA not loaded, submit without it
          this.submitForm()
          resolve()
          return
        }

        grecaptcha.enterprise.ready(() => {
          grecaptcha.enterprise.execute(this.recaptchaEnterpriseSiteKey, { action: 'CREATE' })
            .then(token => {
              this.submitForm(token)
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        })
      })
    },

    submitForm(recaptchaToken) {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = this.formAction
      form.enctype = 'multipart/form-data'
      form.style.display = 'none'

      // CSRF token
      this.addField(form, 'authenticity_token', this.csrfToken)

      // User fields (not signed in)
      if (!this.userSignedIn) {
        this.addField(form, 'topic[user][email]', this.formData.email)
        this.addField(form, 'topic[user][name]', this.formData.name)
      }

      // Topic fields
      this.addField(form, 'topic[name]', this.formData.subject)
      this.addField(form, 'topic[private]', this.effectivePrivate)
      this.addField(form, 'topic[forum_id]', this.effectiveForumId)

      // Team assignment
      if (this.formData.teamList) {
        this.addField(form, 'topic[team_list]', this.formData.teamList)
      }

      // Doc ID
      if (this.docId) {
        this.addField(form, 'topic[doc_id]', this.docId)
      }

      // Honeypot
      this.addField(form, 'topic[url]', this.formData.honeypot)

      // Post body
      this.addField(form, 'topic[posts_attributes][0][body]', this.formData.body)

      // File attachments
      if (this.formData.attachments && this.formData.attachments.length > 0) {
        for (const file of this.formData.attachments) {
          const input = document.createElement('input')
          input.type = 'file'
          input.name = 'topic[posts_attributes][0][attachments][]'
          input.style.display = 'none'

          // Create a DataTransfer to set files on the input
          const dt = new DataTransfer()
          dt.items.add(file)
          input.files = dt.files
          form.appendChild(input)
        }
      }

      // Client ID
      this.addField(form, 'client_id', '')

      // From widget
      if (this.fromWidget) {
        this.addField(form, 'from', 'widget')
      }

      // reCAPTCHA token
      if (recaptchaToken) {
        this.addField(form, 'g-recaptcha-response', recaptchaToken)
      }

      document.body.appendChild(form)
      form.submit()
    },

    addField(form, name, value) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    }
  }
}
</script>

<style scoped>
.new-topic-container {
  max-width: 700px;
  padding: 0 16px;
}

.suggestions-card {
  border-color: #e0e0e0;
}

.suggestion-item {
  min-height: 36px;
}

.suggestion-item:hover {
  background-color: #f5f5f5;
}

/* Remove underline from links */
a {
  text-decoration: none;
}

a:hover {
  text-decoration: none !important;
}
</style>
